import UAParser from 'ua-parser-js';
import _ from 'lodash';

import { NotFoundError } from '../lib/APIClient';
import AuthenticatedAPIClient from '../lib/AuthenticatedAPIClient';
import { resolve, resolveAll } from '../helpers/LinkMapper';
import {FulfilmentType, JourneyDirections} from '../constants/AppConstants';
import appVersion from '../appVersion';
import Order from '../records/Order';
import DeliveryOption from '../records/DeliveryOption';
import AddressService from './AddressService';
import CallingPointsActions from '../actions/CallingPointsActions';
import UserStore from '../stores/UserStore';
import OrderStore from '../stores/OrderStore';
import SmartcardStore from '../stores/SmartcardStore';
import PromoCodeStore from '../stores/PromoCodeStore';
import FeatureFlagStore from '../stores/FeatureFlagStore';
import { throwCorrectOrderError, InteropError } from '../errors/Order';
import { SmartcardStatus } from '../constants/AppConstants';
import Changeover from '../records/order/Changeover';
import FamilyVerificationStore from '../stores/FamilyVerificationStore';

const dataUrl = '/orders';

function serialisePlusBus (plusBus) {

    return {
        outward: {
            origin: plusBus.getIn(['outward', 'origin', 'id'], null),
            destination: plusBus.getIn(['outward', 'destination', 'id'], null)
        },
        'return': {
            origin: plusBus.getIn(['return', 'origin', 'id'], null),
            destination: plusBus.getIn(['return', 'destination', 'id'], null)
        }
    };
}

function serializeLegs(legs, callingPoints) {
    return legs.reduce((result, leg) => {
        const origin = callingPoints.getLegOrigin(leg);
        const destination = callingPoints.getLegDestination(leg);

        if (origin && destination) {
            result.push({
                origin: origin.id,
                destination: destination.id,
                trainOrigin: callingPoints.getTrainOriginStation(leg).id,
                trainDestination: callingPoints.getTrainDestinationStation(leg).id,
                departure: leg.getDepartureTime() && leg.getDepartureTime().format('YYYY-MM-DDTHH:mm:ss'),
                arrival: leg.getArrivalTime() && leg.getArrivalTime().format('YYYY-MM-DDTHH:mm:ss'),
                mode: leg.serviceDetails.mode,
                toc: leg.serviceDetails.getIn(['toc', 'id'], null),
                reservable: leg.serviceDetails.reservable || null,
                facilities: leg.serviceDetails.facilities.toJS(),
                rsid: leg.serviceDetails.retailServiceId || '',
                tuid: leg.serviceDetails.trainUid || '',
                isTemporaryTrain: leg.serviceDetails.isTemporaryTrain,
                transferTime: leg.transferTime
            });
        }
        return result;
    }, []);
}

function serializeJourney (trip, direction) {
    //Season fares & day save require an outwardJourney for some reason, so we add an empty one
    if (!trip.needsJourney() && direction === JourneyDirections.OUTWARD) {
        return { reserve: false, legs: [] };
    }

    const service = trip.getServiceForDirection(direction);
    if (!service || typeof service.id !== 'string') { return { reserve: false, legs: [] }; }

    return CallingPointsActions.getFromService(service).then((callingPoints) => {
        const shouldReserve = trip.reservationPreferences.shouldReserveDirection(direction);

        const legs = serializeLegs(service.journey.legs, callingPoints);

        return {
            reserve: shouldReserve,
            legs,
        };
    });
}

function serializePassenger(trip) {
    return {
        name: trip.photocard.name,
        photocardId: trip.photocard.number
    };
}

export async function serialiseTrip (trip) {
    const outwardFare = trip.getOutwardFare();
    const inwardFare = trip.getInwardFare();

    const shouldReserve = trip.reservationPreferences.shouldReserve();

    let body = {
        fares: {
            outwardSingle: (outwardFare && !outwardFare.isReturn()) ? outwardFare.id : null,
            returnSingle: inwardFare ? inwardFare.id : null,
            "return": (outwardFare && outwardFare.isReturn()) ? outwardFare.id : null,
            plusBus: serialisePlusBus(trip.getPlusBus()),
            season: trip.hasSeasonFare() ? trip.getSeasonFare().id : null,
            fareOnly: trip.hasFareOnlyFare() ? trip.getFareOnlyFare().id : null
        },
        outwardJourney: await serializeJourney(trip, JourneyDirections.OUTWARD),
        returnJourney: await serializeJourney(trip, JourneyDirections.INWARD),
        itso: trip.isSmartcard(),
        reservationPreferences: shouldReserve ? trip.reservationPreferences.toCodes() : null,
    };

    if (trip.bikes >= 1) {
        body.bikes = trip.bikes;
    }

    if (trip.isSmartcard()) {
        const smartcard = SmartcardStore.get('smartcard');
        body.fares.passenger = { smartcardNumber: smartcard.isrn };
    } else if (trip.hasSeasonFare()) {
        body.fares.passenger = serializePassenger(trip);
    } else if (trip.hasRoversRangersFare() && trip.getRoversRangersFare().ticketType.isFamilyRanger() && trip.passengers[0]) {
        body.fares.passenger = { name: trip.passengers[0] };
    }

    const promo = PromoCodeStore.getActive();
    if (promo) {
        body.fares.promoCode = promo.promoCode;
    }

    return body;
}

async function serialiseOrder(order, trip, channel) {
    const parser = new UAParser(navigator.userAgent);
    const user = UserStore.getCurrent();
    const loggedIn = user.isLoggedIn();

    const enableTransPennineStaffRover = FeatureFlagStore.get('enableTransPennineStaffRover');
    const hasTranspennineStaffRover = trip ? trip.isTransPennineStaffRover() : false;
    const familyVerificationEmail = FamilyVerificationStore.getEmail();

    const getEmailAddress = () => {
        // Put Customer User Group email instead of logged in email for TransPennine Staff Rover
        if (enableTransPennineStaffRover && hasTranspennineStaffRover && familyVerificationEmail) {
            return familyVerificationEmail;
        }

        if (loggedIn) {
            return user.email;
        }

        return null;
    };

    return {
        trip: await serialiseTrip(trip),
        channel,
        referrer: null,
        campaign: null,
        device: {
            customer: loggedIn ? `/customers/${user.id}` : null,
            deviceName: 'WEBSITE',
            udid: null,
            appVersion: appVersion.version,
            deviceType: parser.getDevice().type || 'unknown',
        },
        email: getEmailAddress(),
    };
}

function serializeDelivery(order, disableAddress) {
    const { delivery } = order;
    let result = {
        name: delivery.displayName,
        deliveryMethodId: delivery.deliveryMethodId,
        price: delivery.price,
        fulfilmentMethod: delivery.fulfilmentMethod,
        address: delivery.isPostalDelivery() && !disableAddress ? delivery.getAddress().toJS() : null,
        collectionStartDate: null,
        collectionWindowDays: null
    };

    if (delivery.isCollection()) {
        result.collectionLocation = _.get(order, 'delivery.collectionLocation.nlc');
    }

    const enableETicketByPost = FeatureFlagStore.get('enableETicketByPost');
    if (delivery.isPostalDelivery() && enableETicketByPost) {
        result.isPrintedEticket = _.get(order, 'delivery.isPrintedEticket');
    }

    if (delivery.isSmartcard()) {
        let collectionlocation = order.getSmartcardCollectionLocation();
        if (_.isEmpty(collectionlocation)) {
            const smartcard = SmartcardStore.get('smartcard');
            // Fill collection location with origin station data if preload and collection location is "still" empty
            if (!_.isEmpty(smartcard) && smartcard.isAwaitingProduct()) {
                collectionlocation = order.getFirstOrigin();
            }
        }

        Object.assign(result, {
            collectionLocation: collectionlocation ? collectionlocation.nlc : null,
            collectionStartDate: order.getCollectionStartDate().format(),
            collectionWindowDays: delivery.collectionWindowDays
        });

        if (order.hasSmartCarnetTrip() && !result.collectionLocation) {
            result.collectionStartDate = null;
        }
    }

    return result;
}

function serializeLeadPassengers(trips) {
    return trips.reduce((result, trip) => {
        result[trip.id] = String(trip.selfPrintPassenger);
        return result;
    }, {});
}

function serializePhotocards(photocards) {
     return photocards.map(e => ({
                ticketId: e.ticketId,
                photocardDetails: {
                    id: e.id,
                    photocardNumber: e.number,
                    linkCard: e.linkCard,
                    nickName: e.nickname,
                    cardHolder: {
                        title: e.title,
                        firstName: e.firstName,
                        lastName: e.lastName,
                        isOver16: e.isOver16,
                        dob: e.isOver16 ? null : `${e.yearOfBirth}-${String(e.monthOfBirth).padStart(2, '0')}-${String(e.dayOfBirth).padStart(2, '0')}`,
                        address: {
                            line1: e.addressLine1,
                            line2: e.addressLine2,
                            line3: e.addressLine3,
                            town: e.townCity,
                            country: e.country,
                            postCode: e.postcode,
                        }
                    }
                },
                smartcardDetails: null
            }));
}

function serializeSmartcards(trips, smartcard) {
    const user = UserStore.getCurrent();
    return trips.map(e => e.getTickets().map(ticket => ({
        customerId: user.id,
        ticketId: ticket.id,
        photocardDetails: null,
        smartcardDetails: {
            smartcardNumber: smartcard.isrn
        }
    }))).get(0);
}

function serializeLinkedSmartcards(trips, linkedSmartcards) {
    const user = UserStore.getCurrent();

    const associatedSmartcards = SmartcardStore.getAssociatedSmartcards();

    const passengers = trips.flatMap(e =>
        e
            .getTickets()
            .filter(ticket => !!linkedSmartcards.get(ticket.id))
            .map(ticket => {
                const smartcardNumber = linkedSmartcards.getIn([
                    ticket.id,
                    'smartcard',
                    'associatedSmartcardNumber',
                ]) || linkedSmartcards.getIn([
                    ticket.id,
                    'ticket',
                    'passenger',
                    'smartcardNumber',
                ]);

                const associatedCard = associatedSmartcards.find(card => card.associatedSmartcardNumber === smartcardNumber);
                const customerId = (associatedCard && associatedCard.customerId) || user.id;

                return {
                    customerId,
                    ticketId: ticket.id,
                    photocardDetails: null,
                    smartcardDetails: {
                        smartcardNumber: smartcardNumber || null,
                    }
                };
            })
    );

    return passengers;
}

const OrderService = {

    async create (order, trip, channel = "web") {
        try {
            const serializedOrder = await serialiseOrder(order, trip, channel);
            const response = await AuthenticatedAPIClient.post(dataUrl, serializedOrder);
            const deviceToken = response.result.deviceToken;
            return { order: resolve(response.result).as(Order), deviceToken };
        } catch (error) {
            throw throwCorrectOrderError(error);
        }
    },

    async addTrip (order, trip) {
        const url = `${dataUrl}/${order.id}/trips`;
        let output = order;

        try {
            const serializedTrip = await serialiseTrip(trip);
            const response = await AuthenticatedAPIClient.post(url, serializedTrip);

            output = resolve(response.result).as(Order);
        } catch (error) {
            throw throwCorrectOrderError(error);
        }

        return output;
    },

    async deleteTrip(orderTrip) {
        try {
            const response = await AuthenticatedAPIClient.delete(orderTrip.id);
            return response;
        } catch (error) {
            throw throwCorrectOrderError(error);
        }
    },

    async updatePassengers(order, orderTrip, passengers) {
        const url = `${orderTrip.id}/passengers`;

        try {
            await AuthenticatedAPIClient.put(url, passengers.getRequestJSON());
            return true;
        } catch (error) {
            throw throwCorrectOrderError(error);
        }
    },

    async updateFulfilment (order, { disableAddress = false, linkedSmartcards = [] } = {}) {
        const url = `${dataUrl}/${order.id}`;
        const user = UserStore.getCurrent();

        let requestBody = {
            email: user.isLoggedIn() ? user.email : order.email,
            telephoneNumber: user.isLoggedIn() ? user.telephoneNumber : order.telephoneNumber,
            delivery: serializeDelivery(order, disableAddress)
        };

        if (order.delivery.isPrintAtHome()) {
            requestBody.trips = serializeLeadPassengers(order.trips);
        }

        if (order.delivery.hasSeasonTrip(order.trips) && (order.delivery.isCollection() || order.delivery.isPostalDelivery())) {
            if (order.delivery.photocards.every(photocard => photocard.isComplete())) {
                requestBody.passengers = serializePhotocards(order.delivery.photocards);
            }
        }

        if (order.delivery.isSTicket() && order.delivery.sTicketPhotocard.isComplete()) {
            requestBody.passengers = serializePhotocards([order.delivery.sTicketPhotocard]);
        }

        if (order.delivery.isSmartcard()) {
            const smartcard = SmartcardStore.get('smartcard');

            const hasSmartcardAwaitingProduct = smartcard && smartcard.cardStatus === SmartcardStatus.AWAITING_PRODUCT;

            /*
                if no pre-encode, remove invalid smartcards from linked cards
                (pre-encode card appears in linkedSmartcards as an 'invalid' card that is always present, even when no pre-encode exists)
            */

            let validLinkedSmartcards = linkedSmartcards;

            if (!hasSmartcardAwaitingProduct) {
                linkedSmartcards.filter(x => x.get('smartcard') && x.get('smartcard').associatedSmartcardNumber);
            }

            const hasValidSmartcard = smartcard && (smartcard.isAwaitingProduct() || smartcard.isActiveAndAvailableForMoreProducts());

            // Fulfilment for PAH Smartcard only (includes pre-encode for PAH)
            if (hasValidSmartcard && (!validLinkedSmartcards || validLinkedSmartcards.size < 1)) {
                requestBody.passengers = serializeSmartcards(order.trips, smartcard);
            }

            // Linked cards scenario (includes using user's own Smartcard with other Linked cards as this is counted as a 'linked' card)
            if (validLinkedSmartcards && validLinkedSmartcards.size && validLinkedSmartcards.size > 0) {
                requestBody.passengers = serializeLinkedSmartcards(order.trips, validLinkedSmartcards);
            }
        }

        try {
            /*
                Don't update the order for Smartcard fulfilment if there are fewer passengers than tickets as
                this will never be valid and the PUT request will return an error
            */

            if (order.delivery.isSmartcard() && (!requestBody.passengers || (requestBody.passengers.size < order.getTickets().size))) {
                return order;
            }

            const response = await AuthenticatedAPIClient.put(url, requestBody);
            let updated = resolve(response.result).as(Order);

            if (order.delivery.isPostalDelivery() && order.delivery.address) {
                updated = updated.setIn(['delivery', 'address', 'toSave'], order.delivery.address.toSave);

                // Make the storedAddress available to the Payment Complete page
                // so we can update any postal favourites' delivery address
                // with a customer's saved address
                const { storedAddress } = order.delivery;
                const isUserAddressValid = storedAddress ? await AddressService.get(user, storedAddress.get('id')) : null;
                OrderStore.setPostalFulfilmentStoredAddress(storedAddress && isUserAddressValid ? storedAddress : null);
            }
            return updated;
        } catch (error) {
            throw throwCorrectOrderError(error);
        }
    },

    async get (id) {
        try {
            const response = await AuthenticatedAPIClient.get(dataUrl + '/' + id);
            if (response.result) {
                return resolve(response.result).as(Order);
            }
            return null;
        } catch (error) {
            if (error instanceof NotFoundError) {
                return new Order();
            }
            throw error;
        }
    },

    async getDeliveryOptions (id, smartcard) {
        const queryParams = FeatureFlagStore.get('enableSmartcardSeasonEnforcement') === true ? '?isEnforcementApplicable=true' : '';

        const response = await AuthenticatedAPIClient.get(`/delivery/options/${id}${queryParams}`);

        const options = resolveAll(response.result).as(DeliveryOption);

        options.map(opt => {
            if (opt.errors) {
                opt.errors.map(err => {
                    if (err.GTRInteropFlexiFareFound) {
                        throw new InteropError();
                    }
                    return err;
                });
            }
        });

        const price = (option) => option.price;
        const method = (option) => option.getOrder();
        let sorted = options.sortBy(price).sortBy(method);
        if (FeatureFlagStore.get('disableSelfPrint')) {
            sorted = sorted.filter(option => !option.isPrintAtHome());
        }
        if (FeatureFlagStore.get('disableETickets')) {
            sorted = sorted.filter(option => !option.isETicket());
        }
        if (FeatureFlagStore.get('disableTOD')) {
            sorted = sorted.filter(option => !option.isCollection());
        }
        if (FeatureFlagStore.get('disableCCST')) {
            sorted = sorted.filter(option => !option.isPostalDelivery());
        }

        if (!FeatureFlagStore.get('enableProductPreload') && smartcard && smartcard.isAwaitingProduct()) {

            const deliveryWithMockStatus = sorted.map(deliveryMethod => {
                if (deliveryMethod.fulfilmentMethod === FulfilmentType.SMARTCARD) {
                    if ( deliveryMethod.errors ) {
                        return deliveryMethod.set('errors', deliveryMethod.errors.concat({ DisabledPreEncodeError: {}}));
                    } else {
                        return deliveryMethod.set('errors', [{ DisabledPreEncodeError: {}}]);
                    }
                } else {
                    return deliveryMethod;
                }
            });

            return deliveryWithMockStatus;
        }

        return sorted;
    },


    delete (order) {
        return AuthenticatedAPIClient.delete(dataUrl + '/' + order.id);
    },

    async updatePlusBus(order, orderTrip, trip) {
        const url = `${dataUrl}/${order.id}/trips/${orderTrip.getCleanId()}/plusbus`;
        const body = {
            plusBus: serialisePlusBus(trip.getPlusBus())
        };
        try {
            const response = await AuthenticatedAPIClient.put(url, body);
            return resolve(response.result).as(Order);
        } catch (error) {
            throw throwCorrectOrderError(error);
        }
    },

    getTicketUrl (order, ticket) {
        return `/orders/${order.id}/tickets/${ticket.id}.pdf`;
    },

    async deleteSTicketPhoto(userId) {
        try {
            await AuthenticatedAPIClient.delete(`stickets/${userId}/image`);
        } catch (error) {
            throw error;
        }
    },

    async updateSTicketPhoto(userId, binary) {
        try {
            const response = await AuthenticatedAPIClient.postForm(`/stickets/${userId}/image`, {
                photo: new Blob([ binary ])
            });
            return resolve(response.result);
        } catch (error) {
            throw error;
        }
    },

    async sendSTicketPhotoChangeRequest(userId, binary) {
        try {
            const response = await AuthenticatedAPIClient.postForm(`/stickets/${userId}/image/change`, {
                photo: new Blob([ binary ])
            });
            return resolve(response.result);
        } catch (error) {
            throw error;
        }
    },

    async getSTicketPhoto(userId) {
        try {
            // if there's no image added by user - api returns 404, so there's no need to throw an error
            const response = await AuthenticatedAPIClient.get(`/stickets/${userId}/image`, {}, { validErrorStatuses: [404] });
            return response;
        } catch (error) {
            throw error;
        }
    },

    async linkToAccount (token, email) {
        const url = `${dataUrl}/linking`;

        const requestBody = { email };
        const requestOptions = { headers: { 'x-jwt-token': token } };

        try {
            const response = await AuthenticatedAPIClient.post(url, requestBody, requestOptions);
            return resolve(response.result);
        } catch (error) {
            throw error;
        }
    },

    async applyDiscountCode(order, discountCode) {
        const url = `${dataUrl}/${order.id}/discount/trainhugger`;
        const requestBody = { discountCode };

        try {
            const response = await AuthenticatedAPIClient.put(url, requestBody);
            return resolve(response.result).as(Order);
        } catch (error) {
            throw error;
        }
    },

    async removeDiscountCode(order, discountCode) {
        const url = `${dataUrl}/${order.id}/discount/${discountCode}/trainhugger`;

        try {
            const response = await AuthenticatedAPIClient.delete(url);
            return resolve(response.result).as(Order);
        } catch (error) {
            throw error;
        }
    },

    async lock (order) {
        return AuthenticatedAPIClient.put(`/orders/${order.id}/lock`);
    },

    async unlock (order) {
        return AuthenticatedAPIClient.delete(`/orders/${order.id}/lock`);
    },

    async loadChangeovers (order, customerId) {
        const response = await AuthenticatedAPIClient.get(`/changeover/order/${order.id}/customer/${customerId}`);
        return resolveAll(response).as(Changeover);
    },

    async validateRewardCode ({ orderId, customerId, rewardCode }) {
        return AuthenticatedAPIClient.post(`/rewards/${orderId}/validate`, {
            customerId,
            rewardCode
        });
    }
};

export default OrderService;



// WEBPACK FOOTER //
// ./src/services/OrderService.js